/**
 * TransferComposerPreview — sticky right-pane on `/cash/transfers/new`.
 *
 * Mirrors `prototype/cash/transfer-new.html ▸ .composer-side`: four stacked
 * panes,
 *
 *   1. Subtype headline pill ("BANK→CASH — Petty cash top-up").
 *   2. Posting preview block (Dr / Cr lines, V2 GL projection note).
 *   3. After-balance projection bars (from / to row before vs after).
 *   4. Guard chips — cooling-off, approval threshold, imprest ceiling,
 *      partial-card-payment.
 *
 * Pure presentation: the route classifies the subtype, computes the
 * projections + guards from the picked accounts + amount, and passes the
 * shape in. The component just renders.
 *
 * @see prototype/cash/transfer-new.html `.composer-side`
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §6 (Dr/Cr posting)
 */
import { Check, TriangleAlert, ArrowRight } from "lucide-react";
import { cn, ProgressBar } from "@flint/ui";
import { paiseToInr } from "../../../lib/format.ts";
import { SubtypePill } from "./subtype-pill.tsx";
import type { TransferSubtype } from "../lib/classify-subtype.ts";

/** One leg of the Dr / Cr posting preview. */
export interface PostingLine {
  /** `'Dr'` (debit, rose tone) or `'Cr'` (credit, emerald tone). */
  side: "Dr" | "Cr";
  /** Account display, e.g. "Cash on hand". */
  account: string;
  /** Account class — "Asset" / "Liability" / "Equity" etc. */
  accountClass: string;
  /** Leg amount in paise. */
  amountPaise: string;
}

export interface AccountProjection {
  name: string;
  type: "bank" | "cash" | "credit_card";
  beforePaise: string;
  afterPaise: string;
  /** Percentage of "max bar width" the before-bar fills (0-100). */
  beforeBarPct: number;
  /** Percentage of "max bar width" the after-bar fills (0-100). */
  afterBarPct: number;
}

export type GuardStatus = "ok" | "warn" | "bad";

export interface GuardCheck {
  status: GuardStatus;
  title: string;
  detail: string;
}

export interface TransferComposerPreviewProps {
  subtype: TransferSubtype;
  /** One-line copy beneath the subtype pill — describes the legal use. */
  subtypeSummary: string;
  fromAccountName: string;
  toAccountName: string;
  amountPaise: string;
  postingLines: readonly PostingLine[];
  fromProjection: AccountProjection;
  toProjection: AccountProjection;
  guards: readonly GuardCheck[];
}

export function TransferComposerPreview(props: TransferComposerPreviewProps) {
  return (
    <aside className="flex flex-col gap-4">
      {/* Pane 1: subtype headline */}
      <section className="bg-ok-50 border border-ok-100 rounded-md px-4 py-3.5">
        <p className="text-xs uppercase tracking-wider text-muted font-semibold mb-1.5">
          Transfer type
        </p>
        <SubtypePill subtype={props.subtype} />
        <p className="text-xs text-ink-2 mt-2 leading-relaxed">{props.subtypeSummary}</p>
      </section>

      {/* Pane 2: posting preview */}
      <section className="bg-paper border border-line rounded-md overflow-hidden">
        <header className="flex items-center justify-between gap-2 px-4 py-2.5 bg-raised border-b border-line">
          <h4 className="text-xs font-semibold text-ink-2">
            What will post
            <span className="ml-2 inline-block font-mono text-xs text-muted bg-paper border border-line rounded px-1.5 py-0.5">
              cash_movements.kind=transfer
            </span>
          </h4>
          <span className="text-xs font-bold uppercase tracking-wider text-muted border border-line rounded px-1.5 py-0.5">
            CA
          </span>
        </header>
        <ul className="divide-y divide-line">
          {props.postingLines.map((line, i) => (
            <li
              key={`${line.side}-${i}`}
              className="grid grid-cols-[36px_minmax(0,1fr)_auto] gap-3 items-baseline px-4 py-2.5 text-xs"
            >
              <span
                className={cn(
                  "font-mono font-bold uppercase",
                  line.side === "Dr" ? "text-bad" : "text-ok",
                )}
              >
                {line.side}
              </span>
              <span className="text-ink-2 truncate" title={line.account}>
                {line.account}
                <em className="not-italic text-muted text-xs ml-1">{line.accountClass}</em>
              </span>
              <span className="num tabular-nums font-semibold text-right">
                {paiseToInr(line.amountPaise)}
              </span>
            </li>
          ))}
        </ul>
        <footer className="px-4 py-2.5 bg-bg border-t border-line text-xs text-muted leading-relaxed">
          Per ADR-0012 §6. Projects to the V2 General Ledger automatically.
        </footer>
      </section>

      {/* Pane 3: after-balance projection */}
      <section className="bg-paper border border-line rounded-md overflow-hidden">
        <header className="px-4 py-2.5 bg-raised border-b border-line">
          <h4 className="text-xs font-semibold text-ink-2">After this transfer</h4>
        </header>
        <div className="p-4 flex flex-col gap-4">
          <ProjectionRow projection={props.fromProjection} direction="out" />
          <ProjectionRow projection={props.toProjection} direction="in" />
        </div>
      </section>

      {/* Pane 4: guards */}
      {props.guards.length > 0 ? (
        <section className="flex flex-col gap-2">
          {props.guards.map((g, i) => (
            <GuardChip key={i} guard={g} />
          ))}
        </section>
      ) : null}
    </aside>
  );
}

// ── internals ──────────────────────────────────────────────────────

function ProjectionRow({
  projection,
  direction,
}: {
  projection: AccountProjection;
  direction: "in" | "out";
}) {
  const beforeFmt = paiseToInr(projection.beforePaise);
  const afterFmt = paiseToInr(projection.afterPaise);
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <p className="text-xs font-semibold text-ink truncate">{projection.name}</p>
        <span
          className={cn(
            "inline-flex items-center px-1.5 py-px rounded-sm text-xs font-bold uppercase tracking-wider border",
            projection.type === "cash" && "bg-raised text-ink-2 border-line",
            projection.type === "bank" && "bg-sky-50 text-sky-700 border-sky-100",
            projection.type === "credit_card" && "bg-warn-50 text-warn border-warn-100",
          )}
        >
          {projection.type === "credit_card" ? "card" : projection.type}
        </span>
      </div>
      <div className="flex items-baseline gap-2 text-xs num tabular-nums mb-1.5">
        <span className="text-muted">{beforeFmt}</span>
        <ArrowRight size={10} strokeWidth={2} className="text-line-2 shrink-0" aria-hidden />
        <span className={cn("font-semibold", direction === "in" ? "text-ok" : "text-ink-2")}>
          {afterFmt}
        </span>
      </div>
      <div className="relative h-2 bg-bg rounded-full overflow-hidden">
        <ProgressBar
          value={projection.beforeBarPct}
          height={8}
          className="absolute inset-0 opacity-50"
        />
        <ProgressBar value={projection.afterBarPct} height={8} className="absolute inset-0" />
      </div>
    </div>
  );
}

function GuardChip({ guard }: { guard: GuardCheck }) {
  const styles: Record<GuardStatus, { wrap: string; ic: string }> = {
    ok: { wrap: "bg-ok-50 border-ok-100 text-ink-2", ic: "text-ok" },
    warn: { wrap: "bg-warn-50 border-warn-100 text-ink-2", ic: "text-warn" },
    bad: { wrap: "bg-bad-50 border-bad-100 text-ink-2", ic: "text-bad" },
  };
  const s = styles[guard.status];
  const Icon = guard.status === "ok" ? Check : TriangleAlert;
  return (
    <div className={cn("flex items-start gap-2.5 border rounded-sm px-3 py-2", s.wrap)}>
      <span className={cn("inline-grid place-items-center w-5 h-5 shrink-0 mt-px", s.ic)}>
        <Icon size={12} strokeWidth={2.4} aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold">{guard.title}</p>
        <p className="text-xs text-muted mt-0.5 leading-relaxed">{guard.detail}</p>
      </div>
    </div>
  );
}
